import { useState, useMemo, useRef, createContext, useContext, useEffect } from 'react';
import { getInteractionModality, useInteractOutside } from '@react-aria/interactions';
import { useLocale } from '@react-aria/i18n';
import { useKeyboard } from '@react-aria/interactions';
import { focusWithoutScrolling, scrollIntoView, getScrollParent } from '@react-aria/utils';
export function useGridState({ value, minValue, maxValue, columns, onSelect }) {
    const [focusedValue, setFocusedValue] = useState(value);
    const collection = useMemo(() => Array.from({ length: maxValue - minValue + 1 }, (_, offset) => offset + minValue), [maxValue, minValue]);
    // если значение изменилось снаружи, то подстраиваем фокус
    useEffect(() => {
        if (value !== focusedValue) {
            setFocusedValue(value);
        }
    }, [value]);
    const state = useMemo(() => ({
        collection,
        focusedYear: focusedValue,
        focusPrev: () => {
            setFocusedValue(Math.max(focusedValue - 1, minValue));
        },
        focusNext: () => {
            setFocusedValue(Math.min(focusedValue + 1, maxValue));
        },
        focusPrevRow: () => {
            setFocusedValue(Math.max(focusedValue - columns, minValue));
        },
        focusNextRow: () => {
            setFocusedValue(Math.min(focusedValue + columns, maxValue));
        },
        isFocused: (v) => v === focusedValue,
        isSelected: (v) => v === value,
        selectYear: (v) => {
            setFocusedValue(v);
            onSelect(v);
        },
    }), [collection, focusedValue, value, onSelect]);
    return state;
}
export function useGrid({ onClose, columns }, state) {
    const containerRef = useRef(null);
    const { direction } = useLocale();
    const { keyboardProps } = useKeyboard({
        onKeyDown: e => {
            switch (e.key) {
                case 'Escape':
                    e.preventDefault();
                    onClose();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    if (direction === 'rtl') {
                        state.focusNext();
                    }
                    else {
                        state.focusPrev();
                    }
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    if (direction === 'rtl') {
                        state.focusPrev();
                    }
                    else {
                        state.focusNext();
                    }
                    break;
                case 'ArrowUp':
                    state.focusPrevRow();
                    break;
                case 'ArrowDown':
                    state.focusNextRow();
                    break;
                default:
            }
        },
    });
    useInteractOutside({ onInteractOutside: onClose, ref: containerRef });
    const context = useMemo(() => ({ state }), [state]);
    return {
        containerRef,
        keyboardProps,
        context,
        style: { ['--selector-grid-columns']: columns },
    };
}
export const GridContext = createContext(null);
export function useGridCell({ value }) {
    const context = useContext(GridContext);
    if (!context) {
        throw new Error('GridContext lost');
    }
    const { state } = context;
    const ref = useRef(null);
    const isFocused = state.isFocused(value);
    const isSelected = state.isSelected(value);
    useEffect(() => {
        if (isFocused && ref.current) {
            focusWithoutScrolling(ref.current);
            if (getInteractionModality() !== 'pointer') {
                scrollIntoView(getScrollParent(ref.current), ref.current);
            }
        }
    }, [isFocused]);
    useEffect(() => {
        if (isFocused && ref.current) {
            scrollIntoView(getScrollParent(ref.current), ref.current);
        }
    }, []);
    return {
        ref,
        isFocused,
        isSelected,
        onSelect: state.selectYear,
    };
}
